import {
  GENERATED_ROUTE_SCOPE_POLICY,
  WP_GENERATED_ROUTE_SCOPE_POLICY_VERSION,
} from "./generatedRouteScopePolicy";

export const WP_GLOBAL_SCOPE_FETCH_VERSION =
  "wp-r5-phase3-global-scope-fetch-v1";

const ORIGINAL_FETCH_KEY = "__WP_ORIGINAL_FETCH_R5_PHASE3__";
const INSTALLED_KEY = "__WP_GLOBAL_SCOPE_FETCH_INSTALLED__";

const SCOPE_STORAGE = {
  companyId: "wp_scope_company_id",
  deviceUuid: "wp_scope_device_uuid",
  deviceId: "wp_scope_device_id",
  platform: "wp_scope_platform",
  startDate: "wp_scope_start_date",
  endDate: "wp_scope_end_date",
  status: "wp_scope_status",
  severity: "wp_scope_severity",
};

function escapeRegex(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function templateToRegex(template) {
  const normalized =
    template.length > 1 && template.endsWith("/")
      ? template.slice(0, -1)
      : template;

  const parts = normalized.split("/").map((part) => {
    if (/^\{[^}]+\}$/.test(part)) return "[^/]+";
    return escapeRegex(part);
  });

  return new RegExp(`^${parts.join("/")}/?$`);
}

const compiledPolicy = GENERATED_ROUTE_SCOPE_POLICY.map((entry) => ({
  ...entry,
  regex: templateToRegex(entry.pathTemplate),
}));

function getScopeSnapshot() {
  const role = localStorage.getItem("role") || "";
  const sessionCompanyId = localStorage.getItem("company_id") || "";
  const selectedCompanyId =
    role === "super_admin"
      ? localStorage.getItem(SCOPE_STORAGE.companyId) || sessionCompanyId
      : sessionCompanyId;

  return {
    companyId: selectedCompanyId,
    deviceUuid: localStorage.getItem(SCOPE_STORAGE.deviceUuid) || "",
    deviceId: localStorage.getItem(SCOPE_STORAGE.deviceId) || "",
    platform: localStorage.getItem(SCOPE_STORAGE.platform) || "all",
    startDate: localStorage.getItem(SCOPE_STORAGE.startDate) || "",
    endDate: localStorage.getItem(SCOPE_STORAGE.endDate) || "",
    status: localStorage.getItem(SCOPE_STORAGE.status) || "all",
    severity: localStorage.getItem(SCOPE_STORAGE.severity) || "all",
  };
}

function findPolicy(pathname, method) {
  const upper = String(method || "GET").toUpperCase();
  return compiledPolicy.find(
    (entry) => entry.method === upper && entry.regex.test(pathname)
  );
}

function maybeSet(params, key, value) {
  if (!value || params.has(key)) return;
  params.set(key, String(value));
}

function applyScopeParams(url, policy, scope) {
  const supported = new Set(policy.params || []);

  if (supported.has("company_id")) maybeSet(url.searchParams, "company_id", scope.companyId);
  if (supported.has("device_uuid")) maybeSet(url.searchParams, "device_uuid", scope.deviceUuid);
  if (supported.has("device_id")) maybeSet(url.searchParams, "device_id", scope.deviceId);

  if (
    supported.has("platform") &&
    scope.platform &&
    scope.platform !== "all"
  ) {
    maybeSet(url.searchParams, "platform", scope.platform);
  }

  for (const name of ["start_date", "from_date", "date_from"]) {
    if (supported.has(name)) maybeSet(url.searchParams, name, scope.startDate);
  }

  for (const name of ["end_date", "to_date", "date_to"]) {
    if (supported.has(name)) maybeSet(url.searchParams, name, scope.endDate);
  }

  if (supported.has("status") && scope.status !== "all") {
    maybeSet(url.searchParams, "status", scope.status);
  }

  if (supported.has("severity") && scope.severity !== "all") {
    maybeSet(url.searchParams, "severity", scope.severity);
  }

  return url;
}

function scopedUrl(input, init = {}) {
  const method =
    init?.method ||
    (typeof Request !== "undefined" && input instanceof Request
      ? input.method
      : "GET");

  if (String(method).toUpperCase() !== "GET") return null;

  const raw =
    typeof input === "string" || input instanceof URL
      ? String(input)
      : typeof Request !== "undefined" && input instanceof Request
      ? input.url
      : "";

  if (!raw) return null;

  let url;
  try {
    url = new URL(raw, window.location.origin);
  } catch (_error) {
    return null;
  }

  const policy = findPolicy(url.pathname, method);
  if (!policy) return null;

  return applyScopeParams(url, policy, getScopeSnapshot());
}

export function installGlobalScopeFetchInterceptor() {
  if (
    typeof window === "undefined" ||
    typeof window.fetch !== "function"
  ) {
    return false;
  }

  if (window[INSTALLED_KEY]) return true;

  const originalFetch =
    window[ORIGINAL_FETCH_KEY] || window.fetch.bind(window);

  window[ORIGINAL_FETCH_KEY] = originalFetch;

  window.fetch = function wpScopedFetch(input, init) {
    const nextUrl = scopedUrl(input, init || {});
    if (!nextUrl) return originalFetch(input, init);

    if (
      typeof Request !== "undefined" &&
      input instanceof Request
    ) {
      return originalFetch(
        new Request(nextUrl.toString(), input),
        init
      );
    }

    return originalFetch(nextUrl.toString(), init);
  };

  window[INSTALLED_KEY] = {
    version: WP_GLOBAL_SCOPE_FETCH_VERSION,
    routePolicyVersion: WP_GENERATED_ROUTE_SCOPE_POLICY_VERSION,
    routePolicyCount: GENERATED_ROUTE_SCOPE_POLICY.length,
  };

  return true;
}
